import './styles.scoped.css'
import {useState, useEffect} from 'react'
import {Typography, Button, IconButton, Menu, MenuItem, Skeleton} from '@mui/material'
import {ClientContactsForm, EmergencyContactForm} from './components'
import {useMobile} from '../../hooks'
import {Edit, Add, Phone, Email, MoreVert, LocationOn} from '@mui/icons-material'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {clientApi} from '../../services'
import {useParams} from 'react-router-dom'
import {parseError} from '../../utils/helpers'
import {format} from 'date-fns'
import {SkeletonContainer, TextSkeleton} from '../../components'

export default () => {
    const {setLoading, setMessageBox} = useGlobalStates()
    const [openClientContactsForm, setOpenClientContactsForm] = useState(false)
    const [openEmergencyContactForm, setOpenEmergencyContactForm] = useState(false)
    const [primaryContact, setPrimaryContact] = useState(null)
    const [emergencyContacts, setEmergencyContacts] = useState(null)
    const [anchorEl, setAnchorEl] = useState(null)
    const [selectedRow, setSelectedRow] = useState(null)
    const {mobile} = useMobile()
    const {clientId} = useParams()
    const [skeleton, setSkeleton] = useState(true)

    useEffect(() => {
        loadData(true)
    }, [])

    // The first load shows the skeleton; refetches after a save or removal keep the global spinner.
    const loadData = (initial = false) => {
        const done = () => (initial ? setSkeleton(false) : setLoading(false))
        if (!initial) setLoading(true)
        clientApi
            .getClientContacts(clientId)
            .then(({data: {data}}) => {
                done()
                setPrimaryContact(data?.find(item => item.type == 'primary'))
                setEmergencyContacts(data?.filter(item => item.type == 'emergency'))
            })
            .catch(error => {
                done()
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    const menuOpen = (e, row) => {
        setAnchorEl(e.currentTarget)
        setSelectedRow(row)
    }

    const menuClose = () => {
        setAnchorEl(null)
    }

    const updateClientContact = () => {
        setOpenClientContactsForm(true)
    }

    const addEmergencyContact = () => {
        setSelectedRow(null)
        setOpenEmergencyContactForm(true)
    }

    const updateEmergencyContact = () => {
        setOpenEmergencyContactForm(true)
        menuClose()
    }

    const removeEmergencyContact = () => {
        menuClose()
        setLoading(true)
        clientApi
            .removeClientContact(clientId, selectedRow?.id)
            .then(() => {
                setLoading(false)
                setMessageBox({
                    status: 'success',
                    title: 'Client Emergency Contact Removed Successfully',
                    description: `The client’s emergency contact details for ${selectedRow?.name} have been removed.`,
                })
                loadData()
            })
            .catch(error => {
                setLoading(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    return (
        <>
            {!mobile && (
                <>
                    <Typography variant="h6" color="text.primary">
                        Contact Information
                    </Typography>
                    <br />
                    <br />
                </>
            )}
            <div className="title">
                <Typography variant="overline" color="text.primary">
                    CLIENT CONTACT
                </Typography>
                <Button variant="outlined" startIcon={<Edit />} onClick={updateClientContact}>
                    Update Client Contacts
                </Button>
            </div>
            <br />
            <div className="client-container">
                {skeleton && (
                    <SkeletonContainer>
                        {[110, 110, 190].map((width, index) => (
                            <div key={index}>
                                <br />
                                <div className="form">
                                    <Skeleton variant="circular" width={24} height={24} />
                                    <TextSkeleton width={width} fontSize={16} />
                                    <TextSkeleton width={50} fontSize={12} />
                                </div>
                            </div>
                        ))}
                    </SkeletonContainer>
                )}
                {primaryContact?.phones?.map(phone => (
                    <div key={phone?.id}>
                        <br />
                        <div className="form">
                            <Phone fontSize="medium" sx={theme => ({color: theme.palette.primary.light})} />
                            <Typography variant="body1" color="text.primary">
                                {phone?.number}
                            </Typography>
                            <Typography variant="overline" color="text.disabled">
                                ({phone?.type})
                            </Typography>
                        </div>
                    </div>
                ))}
                {primaryContact?.emails?.map(email => (
                    <div key={email?.id}>
                        <br />
                        <div className="form">
                            <Email fontSize="medium" sx={theme => ({color: theme.palette.primary.light})} />
                            <Typography variant="body1" color="text.primary">
                                {email?.email}
                            </Typography>
                            <Typography variant="overline" color="text.disabled">
                                ({email?.type})
                            </Typography>
                        </div>
                    </div>
                ))}
                <br />
            </div>
            <br />
            <br />
            <div className="title">
                <Typography variant="overline" color="text.primary">
                    EMERGENCY CONTACT
                </Typography>
                <Button variant="outlined" startIcon={<Add />} onClick={addEmergencyContact}>
                    Add Emergency Contact
                </Button>
            </div>
            {skeleton &&
                [0, 1].map(index => (
                    <div key={index} aria-hidden="true">
                        <br />
                        <div className="emergency-card">
                            <div className="card-header">
                                <TextSkeleton width={60} fontSize={12} />
                                <Skeleton variant="circular" width={24} height={24} style={{margin: 8}} />
                            </div>
                            <TextSkeleton width={index ? 140 : 170} fontSize={20} />
                            <br />
                            <div className="em-content">
                                <div className="form">
                                    <Skeleton variant="circular" width={24} height={24} />
                                    <TextSkeleton width={100} fontSize={16} />
                                </div>
                                <div className="form">
                                    <Skeleton variant="circular" width={24} height={24} />
                                    <TextSkeleton width={index ? 150 : 180} fontSize={16} />
                                </div>
                            </div>
                            <br />
                            <div className="form2">
                                <Skeleton variant="circular" width={24} height={24} />
                                <TextSkeleton width={260} fontSize={16} />
                            </div>
                            <br />
                            <TextSkeleton width={160} fontSize={16} />
                        </div>
                    </div>
                ))}
            {emergencyContacts?.map(emergencyContact => (
                <div key={emergencyContact?.id}>
                    <br />
                    <div className="emergency-card">
                        <div className="card-header">
                            <Typography variant="overline" color="primary.dark">
                                {emergencyContact?.relationship}
                            </Typography>
                            <IconButton onClick={e => menuOpen(e, emergencyContact)}>
                                <MoreVert />
                            </IconButton>
                        </div>
                        <Typography variant="h6" color="text.primary">
                            {emergencyContact?.name}
                        </Typography>
                        <br />
                        <div className="em-content">
                            <div className="form">
                                <Phone fontSize="medium" sx={theme => ({color: theme.palette.primary.main})} />
                                <Typography variant="body1" color="text.primary">
                                    {emergencyContact?.phones?.find(item => item.is_primary)?.number}
                                </Typography>
                            </div>
                            |
                            <div className="form">
                                <Email fontSize="medium" sx={theme => ({color: theme.palette.primary.main})} />
                                <Typography variant="body1" color="text.primary">
                                    {emergencyContact?.emails?.find(item => item.is_primary)?.email}
                                </Typography>
                            </div>
                        </div>
                        <br />
                        <div className="form2">
                            <LocationOn fontSize="medium" sx={theme => ({color: theme.palette.primary.main})} />
                            <Typography variant="body1" color="text.primary" style={{maxWidth: 300}}>
                                {`${emergencyContact?.addresses?.find(item => item.is_primary)?.street_line1}${emergencyContact?.addresses?.find(item => item.is_primary)?.street_line2 ? ', ' + emergencyContact?.addresses?.find(item => item.is_primary)?.street_line2 : ''}, ${
                                    emergencyContact?.addresses?.find(item => item.is_primary)?.city
                                }, ${emergencyContact?.addresses?.find(item => item.is_primary)?.state}, ${emergencyContact?.addresses?.find(item => item.is_primary)?.zip_code}`}
                            </Typography>
                        </div>
                        <br />
                        <Typography variant="body1" color="text.secondary">
                            {'Added date: ' + format(new Date(emergencyContact?.created_at), 'MM/dd/yyyy')}
                        </Typography>
                    </div>
                </div>
            ))}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={menuClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}>
                <div>
                    <MenuItem onClick={updateEmergencyContact}>Update Contact</MenuItem>
                    <MenuItem onClick={removeEmergencyContact} sx={theme => ({color: theme.palette.error.main})}>
                        Remove Contact
                    </MenuItem>
                </div>
            </Menu>
            <ClientContactsForm open={openClientContactsForm} setOpen={setOpenClientContactsForm} data={primaryContact} loadData={loadData} />
            <EmergencyContactForm open={openEmergencyContactForm} setOpen={setOpenEmergencyContactForm} data={selectedRow} loadData={loadData} />
        </>
    )
}
